import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText } from 'lucide-react';
import TexHubLogo from '@/components/brand/TexHubLogo';
import { useLocale } from '@/contexts/LocaleContext';

const TermsPage = () => {
  const { isUSA } = useLocale();
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <TexHubLogo size="small" />
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-4 max-w-4xl py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              {isUSA ? 'Terms and Conditions' : 'Términos y Condiciones'}
            </h1>
            <p className="text-slate-500">{isUSA ? 'Last updated: March 2025' : 'Última actualización: Marzo 2025'}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-sm space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '1. Acceptance of Terms' : '1. Aceptación de los Términos'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'By accessing and using the TexHub platform ("the Platform"), you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you may not access the service.'
                : 'Al acceder y utilizar la plataforma TexHub ("la Plataforma"), usted acepta estar sujeto a estos Términos y Condiciones. Si no está de acuerdo con alguna parte de estos términos, no podrá acceder al servicio.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '2. Description of Service' : '2. Descripción del Servicio'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'TexHub is a B2B platform that connects suppliers, manufacturers and buyers in the textile industry. Our services include:'
                : 'TexHub es una plataforma B2B que conecta a proveedores, maquiladores y compradores de la industria textil. Nuestros servicios incluyen:'}
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-3 space-y-2">
              <li>{isUSA ? 'Catalog of textile products and services' : 'Catálogo de productos y servicios textiles'}</li>
              <li>{isUSA ? 'Quotation and messaging system' : 'Sistema de cotizaciones y mensajería'}</li>
              <li>{isUSA ? 'Surplus and overstock publishing' : 'Publicación de excedentes y saldos'}</li>
              <li>{isUSA ? 'AI visualization tools (TexVision)' : 'Herramientas de visualización con inteligencia artificial (TexVision)'}</li>
              <li>{isUSA ? 'Community blog' : 'Blog comunitario'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '3. Registration and Accounts' : '3. Registro y Cuentas'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'To use certain services of the Platform, you must register and create an account. You are responsible for:'
                : 'Para utilizar ciertos servicios de la Plataforma, debe registrarse y crear una cuenta. Usted es responsable de:'}
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-3 space-y-2">
              <li>{isUSA ? 'Providing accurate and up-to-date information' : 'Proporcionar información precisa y actualizada'}</li>
              <li>{isUSA ? 'Maintaining the confidentiality of your password' : 'Mantener la confidencialidad de su contraseña'}</li>
              <li>{isUSA ? 'All activities that occur under your account' : 'Todas las actividades que ocurran bajo su cuenta'}</li>
              <li>{isUSA ? 'Immediately notifying any unauthorized use' : 'Notificar inmediatamente cualquier uso no autorizado'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '4. Subscription Plans and Payments' : '4. Planes de Suscripción y Pagos'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'TexHub offers different subscription plans with varying levels of access and features.'
                : 'TexHub ofrece diferentes planes de suscripción con distintos niveles de acceso y funcionalidades.'}
            </p>
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">{isUSA ? '4.1 Billing' : '4.1 Facturación'}</h3>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'Payments are securely processed through Stripe. By subscribing to a paid plan, you authorize TexHub to charge the corresponding fee on a recurring basis according to the selected billing cycle (monthly).'
                : 'Los pagos se procesan de forma segura a través de Stripe. Al suscribirse a un plan de pago, autoriza a TexHub a cobrar la tarifa correspondiente de forma recurrente según el ciclo de facturación seleccionado (mensual).'}
            </p>
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">{isUSA ? '4.2 Free Plan' : '4.2 Plan Gratuito'}</h3>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'TexHub offers a free plan that allows you to create an account and add up to 5 products to your catalog without providing payment information. To access additional features such as unlimited products, online store, and TexVision AI, you must upgrade to the PRO plan.'
                : 'TexHub ofrece un plan gratuito que permite crear una cuenta y agregar hasta 5 productos a tu catálogo sin proporcionar información de pago. Para acceder a funciones adicionales como productos ilimitados, tienda en línea y TexVision AI, debe actualizar al plan PRO.'}
            </p>
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">{isUSA ? '4.3 Cancellation' : '4.3 Cancelación'}</h3>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'You may cancel your subscription at any time from your control panel. Cancellation will be effective at the end of the current billing period and no prorated refunds will be made.'
                : 'Puede cancelar su suscripción en cualquier momento desde su panel de control. La cancelación será efectiva al final del período de facturación actual y no se realizarán reembolsos prorrateados.'}
            </p>
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">{isUSA ? '4.4 Price Changes' : '4.4 Cambios de Precio'}</h3>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'TexHub reserves the right to modify plan prices. Changes will be notified at least 30 days in advance.'
                : 'TexHub se reserva el derecho de modificar los precios de los planes. Los cambios serán notificados con al menos 30 días de anticipación.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '5. Acceptable Use' : '5. Uso Aceptable'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA ? 'When using the Platform, you agree not to:' : 'Al usar la Plataforma, usted acepta no:'}
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-3 space-y-2">
              <li>{isUSA ? 'Violate applicable laws or regulations' : 'Violar leyes o regulaciones aplicables'}</li>
              <li>{isUSA ? 'Publish false, misleading or fraudulent content' : 'Publicar contenido falso, engañoso o fraudulento'}</li>
              <li>{isUSA ? 'Infringe intellectual property rights of third parties' : 'Infringir derechos de propiedad intelectual de terceros'}</li>
              <li>{isUSA ? 'Transmit viruses or malicious code' : 'Transmitir virus o código malicioso'}</li>
              <li>{isUSA ? 'Attempt unauthorized access to systems or accounts' : 'Intentar acceder sin autorización a sistemas o cuentas'}</li>
              <li>{isUSA ? 'Use the platform for spam or unsolicited communications' : 'Usar la plataforma para spam o comunicaciones no solicitadas'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '6. Intellectual Property' : '6. Propiedad Intelectual'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'All content on the Platform, including texts, graphics, logos, icons, images and software, is the property of TexHub or its licensors and is protected by intellectual property laws and international treaties.'
                : 'Todo el contenido de la Plataforma, incluyendo textos, gráficos, logos, iconos, imágenes y software, es propiedad de TexHub o sus licenciantes y está protegido por las leyes de propiedad intelectual y tratados internacionales.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '7. User Content' : '7. Contenido del Usuario'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA ? 'By posting content on the Platform, you:' : 'Al publicar contenido en la Plataforma, usted:'}
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-3 space-y-2">
              <li>{isUSA ? 'Retain ownership of your content' : 'Mantiene la propiedad de su contenido'}</li>
              <li>{isUSA ? 'Grant TexHub a license to use, display and distribute such content' : 'Otorga a TexHub una licencia para usar, mostrar y distribuir dicho contenido'}</li>
              <li>{isUSA ? 'Warrant that you have the necessary rights to publish the content' : 'Garantiza que tiene los derechos necesarios para publicar el contenido'}</li>
              <li>{isUSA ? 'Accept that TexHub may moderate or remove content that violates these terms' : 'Acepta que TexHub puede moderar o eliminar contenido que viole estos términos'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '8. Limitation of Liability' : '8. Limitación de Responsabilidad'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'TexHub provides the Platform "as is" and does not guarantee that it will be free of errors or interruptions. In no event shall TexHub be liable for indirect, incidental, special or consequential damages resulting from the use or inability to use the Platform.'
                : 'TexHub proporciona la Plataforma "tal cual" y no garantiza que estará libre de errores o interrupciones. En ningún caso TexHub será responsable por daños indirectos, incidentales, especiales o consecuentes que resulten del uso o la imposibilidad de usar la Plataforma.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '9. Governing Law' : '9. Ley Aplicable'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'These Terms and Conditions shall be governed by and construed in accordance with the laws of the United Mexican States. Any dispute shall be submitted to the jurisdiction of the competent courts of Mexico City.'
                : 'Estos Términos y Condiciones se regirán e interpretarán de acuerdo con las leyes de los Estados Unidos Mexicanos. Cualquier disputa se someterá a la jurisdicción de los tribunales competentes de la Ciudad de México.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '10. Contact' : '10. Contacto'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'For questions about these Terms and Conditions, you can contact us at:'
                : 'Para preguntas sobre estos Términos y Condiciones, puede contactarnos en:'}
            </p>
            <p className="text-slate-600 mt-3">
              <strong>Email:</strong> Info@texhub.com.mx
            </p>
          </section>
        </div>

        <div className="mt-8 text-center">
          <Link to="/">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {isUSA ? 'Back to home' : 'Volver al inicio'}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
